/* Diesel Proof — product description tabs.
   Desktop: real tabs (roving tabindex, arrow-key navigation).
   Mobile:  the same panels behave as an accordion via .dp-tabs__accordion-toggle.
   Both views share one set of panels so there is no duplicated markup. */
if (!customElements.get('dp-product-tabs')) {
  customElements.define(
    'dp-product-tabs',
    class DpProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('.dp-tabs__tab'));
        this.panels = Array.from(this.querySelectorAll('.dp-tabs__panel'));
        this.desktop = window.matchMedia('(min-width: 750px)');

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });

        this.panels.forEach((panel, index) => {
          const toggle = panel.querySelector('.dp-tabs__accordion-toggle');
          if (toggle) toggle.addEventListener('click', () => this.toggleAccordion(index));
        });

        this.onBreakpoint = () => this.applyMode();
        this.desktop.addEventListener('change', this.onBreakpoint);
        this.applyMode();
      }

      disconnectedCallback() {
        if (this.onBreakpoint) this.desktop.removeEventListener('change', this.onBreakpoint);
      }

      applyMode() {
        if (this.desktop.matches) {
          // Exactly one panel open, driven by the active tab.
          const active = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
          this.select(active === -1 ? 0 : active);
        } else {
          // Accordion: honour each toggle's own expanded state.
          this.panels.forEach((panel) => {
            const toggle = panel.querySelector('.dp-tabs__accordion-toggle');
            const open = toggle ? toggle.getAttribute('aria-expanded') === 'true' : true;
            panel.hidden = false;
            this.setPanelBody(panel, open);
          });
        }
      }

      setPanelBody(panel, open) {
        const body = panel.querySelector('.dp-tabs__panel-inner');
        if (body) body.hidden = !open;
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });

        this.panels.forEach((panel, i) => {
          const active = i === index;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
          this.setPanelBody(panel, true);
          const toggle = panel.querySelector('.dp-tabs__accordion-toggle');
          if (toggle) toggle.setAttribute('aria-expanded', active ? 'true' : 'false');
        });
      }

      toggleAccordion(index) {
        const panel = this.panels[index];
        const toggle = panel.querySelector('.dp-tabs__accordion-toggle');
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.setPanelBody(panel, open);
      }

      onKeydown(event, index) {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      }

    }
  );
}

/* Read more / show all on long spec values. Delegated at the document level
   because the specs table renders outside <dp-product-tabs>, and so rows
   swapped in on a variant change keep working without re-binding. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('.dp-spec__more');
  if (!button) return;

  const wrap = button.closest('[data-dp-spec-truncate]');
  if (!wrap) return;

  const expanded = button.getAttribute('aria-expanded') === 'true';
  const short = wrap.querySelector('.dp-spec__short');
  const full = wrap.querySelector('.dp-spec__full');

  if (wrap.classList.contains('dp-spec__truncate--list')) {
    wrap.classList.toggle('is-expanded', !expanded);
  } else {
    if (short) short.hidden = !expanded;
    if (full) full.hidden = expanded;
  }

  button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  button.textContent = expanded
    ? button.dataset.more || 'Read more'
    : button.dataset.less || 'Read less';
});

/* Specs follow the selected variant. Most spec metafields live on variants,
   and this section sits outside main-product, so Dawn's variant swap doesn't
   touch it. On variant change, re-fetch just this section for the new variant
   and swap the aside (specs + install details). Only runs on products that
   actually have variants. */
(() => {
  const aside = document.querySelector('[data-dp-specs-aside][data-dp-specs-variant-aware]');
  if (!aside || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  const sectionId = aside.dataset.sectionId;
  let controller;

  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const variantId = data && data.variant && data.variant.id;
    if (!variantId) return;

    if (controller) controller.abort();
    controller = new AbortController();

    const url = `${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`;
    fetch(url, { signal: controller.signal })
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector('[data-dp-specs-aside]');
        const current = document.querySelector(`[data-dp-specs-aside][data-section-id="${sectionId}"]`);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.warn('[dp-specs] variant refresh failed', error);
      });
  });
})();
